/**
 * Stale-while-revalidate cache for control-centre "actions required" data.
 *
 * Keyed by businessId. Holds data in module scope (survives client-side navigation) and,
 * when given a `persistKey`, mirrors it to localStorage so a cold start (app launch / page
 * reload) can render the last-known actions in the very first paint — no layout jump while
 * the hooks refresh in the background.
 */
export interface BusinessCache<T> {
  get: (businessId: string) => T | undefined;
  has: (businessId: string) => boolean;
  set: (businessId: string, value: T) => void;
}

export interface BusinessCacheOptions<T> {
  /** When set, the cache is mirrored to localStorage under `cc-actions:<persistKey>:<businessId>`. */
  persistKey?: string;
  /** Custom revive step for persisted JSON (e.g. to rebuild Date fields). Defaults to identity. */
  reviveStored?: (parsed: unknown) => T;
}

function hasLocalStorage(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch {
    return false;
  }
}

export function createBusinessCache<T>(options: BusinessCacheOptions<T> = {}): BusinessCache<T> {
  const { persistKey, reviveStored } = options;
  const store = new Map<string, T>();
  const persistEnabled = !!persistKey && hasLocalStorage();
  const storageKey = (businessId: string) => `cc-actions:${persistKey}:${businessId}`;

  const readPersisted = (businessId: string): { found: boolean; value?: T } => {
    if (!persistEnabled) return { found: false };
    try {
      const raw = window.localStorage.getItem(storageKey(businessId));
      if (raw == null) return { found: false };
      const parsed = JSON.parse(raw) as unknown;
      const value = reviveStored ? reviveStored(parsed) : (parsed as T);
      store.set(businessId, value);
      return { found: true, value };
    } catch {
      return { found: false };
    }
  };

  return {
    has: (businessId) => {
      if (store.has(businessId)) return true;
      return readPersisted(businessId).found;
    },
    get: (businessId) => {
      if (store.has(businessId)) return store.get(businessId);
      return readPersisted(businessId).value;
    },
    set: (businessId, value) => {
      store.set(businessId, value);
      if (persistEnabled) {
        try {
          window.localStorage.setItem(storageKey(businessId), JSON.stringify(value));
        } catch {
          // Quota / private-mode failures are non-fatal — in-memory cache still applies.
        }
      }
    },
  };
}
